/**
 * Styled Components Theme
 * 
 * This file defines the default theme for styled-components.
 * It maps the design system theme to the frontend's Theme interface.
 */

import colors from '../design-system/colors';
import typography from '../design-system/typography';
import spacingSystem from '../design-system/spacing';
import { Theme } from '../types/styled';

// Debug log to check what we're getting
if (typeof window !== 'undefined') {
  console.log('[Theme Debug] spacingSystem:', spacingSystem);
  console.log('[Theme Debug] colors object:', colors);
  console.log('[Theme Debug] colors.secondary:', colors?.secondary);
}

/**
 * Convert lineHeight numbers to strings
 */
const convertLineHeight = (lineHeight: Record<string, number>): Record<string, string> => {
  return Object.entries(lineHeight).reduce((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {} as Record<string, string>);
};

/**
 * Flatten spacing object to simple key-value pairs
 */
const flattenSpacing = (spacing: any): Record<string, string> => {
  // Default spacing values
  const defaultSpacing = {
    '0': '0',
    '1': '1px',
    '2': '2px',
    '4': '4px',
    '8': '8px',
    '12': '12px',
    '16': '16px',
    '20': '20px',
    '24': '24px',
    '32': '32px',
    '40': '40px',
    '48': '48px',
    '56': '56px',
    '64': '64px',
    '80': '80px',
    '96': '96px',
    '128': '128px',
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
    '4xl': '96px',
    '5xl': '128px',
  };

  if (!spacing) {
    console.warn('[Theme] No spacing system provided, using defaults');
    return defaultSpacing;
  }
  
  if (spacing.spacing && typeof spacing.spacing === 'object') {
    return { ...defaultSpacing, ...spacing.spacing };
  }
  
  if (typeof spacing === 'object') {
    return { ...defaultSpacing, ...spacing };
  }
  
  console.warn('[Theme] Invalid spacing format, using defaults');
  return defaultSpacing;
};

/**
 * Default theme for styled-components
 * This theme is constructed from the design system's exported primitives
 * following the proper module boundaries (Module M7 - Integration Points)
 */
const styledTheme: Theme = {
  colors: {
    ...colors,
    theme: {
      background: {
        primary: '#FFFFFF',
        secondary: '#F9FAFB',
        tertiary: '#E5E7EB',
      },
      text: {
        primary: colors?.neutral?.['900'] || '#212121',
        secondary: colors?.neutral?.['700'] || '#616161',
        tertiary: colors?.neutral?.['500'] || '#9E9E9E',
      },
      border: {
        light: colors?.neutral?.['200'] || '#EEEEEE',
        medium: colors?.neutral?.['300'] || '#E0E0E0',
        dark: colors?.neutral?.['400'] || '#BDBDBD',
      },
    },
  },
  typography: {
    fontFamily: {
      primary: typography?.fontFamily?.primary || 'Inter, system-ui, -apple-system, sans-serif',
      secondary: typography?.fontFamily?.secondary || 'Inter, system-ui, -apple-system, sans-serif',
    },
    fontSize: typography?.fontSize || {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
    },
    fontWeight: typography?.fontWeight || {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: convertLineHeight(typography?.lineHeight || {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    }),
  },
  spacing: flattenSpacing(spacingSystem),
  layout: {
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
    },
    maxWidth: '1280px',
  },
  inset: {},
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  },
  transitions: {
    fast: '150ms ease-in-out',
    medium: '200ms ease-in-out',
    slow: '300ms ease-in-out',
  },
  breakpoints: {
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
};

// Validate theme structure
if (typeof window !== 'undefined') {
  console.log('[Theme Debug] Final theme structure:', {
    hasColors: !!styledTheme.colors,
    hasSecondary: !!styledTheme.colors?.secondary,
    secondaryKeys: Object.keys(styledTheme.colors?.secondary || {}),
    colorKeys: Object.keys(styledTheme.colors || {})
  });
}

// Dark theme variant (for now, same as light theme)
// TODO: Implement proper dark theme colors
export const darkTheme: Theme = styledTheme;

export default styledTheme;